// month names
const months = [
  { number: 1, description: "January", shortDescription: "Jan" },
  { number: 2, description: "February", shortDescription: "Feb" },
  { number: 3, description: "March", shortDescription: "Mar" },
  { number: 4, description: "April", shortDescription: "Apr" },
  { number: 5, description: "May", shortDescription: "May" },
  { number: 6, description: "June", shortDescription: "Jun" },
  { number: 7, description: "July", shortDescription: "Jul" },
  { number: 8, description: "August", shortDescription: "Aug" },
  { number: 9, description: "September", shortDescription: "Sep" },
  { number: 10, description: "October", shortDescription: "Oct" },
  { number: 11, description: "November", shortDescription: "Nov" },
  { number: 12, description: "December", shortDescription: "Dec" },
];

// enum + description for subscription types
const subscriptionTypeEnums = [
  { description: "Free", enum: 0 },
  { description: "Early Bird", enum: 1 },
  { description: "VIP", enum: 2 },
  { description: "Enterprise", enum: 3 },
  { description: "Startup", enum: 4 },
  { description: "Bootstrap", enum: 5 },
];

// enum + description combos for billing cycles
const billingCycleEnums = [
  { description: "monthly", enum: 0 },
  { description: "annually", enum: 1 },
  { description: "never", enum: 2 },
  { description: "enterprisePlus", enum: 3 },
];

// enum + description combos for billing transaction types
const billingTransactionTypeEnums = [
  { description: "none", enum: 0 },
  { description: "charge", enum: 1 },
  { description: "refund", enum: 2 },
];

// from https://stripe.com/docs/api/cards/object#card_object-brand
const creditCardBrands = [
  { brand: "American Express", icon: "cc-amex", regex: /^3[47][0-9]{13}$/ },
  {
    brand: "Diners Club",
    icon: "cc-diners-club",
    regex: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
  },
  {
    brand: "Discover",
    icon: "cc-discover",
    regex: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
  },
  { brand: "JCB", icon: "cc-jcb", regex: /^(?:2131|1800|35\d{3})\d{11}$/ },
  {
    brand: "MasterCard",
    icon: "cc-mastercard",
    regex: /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/,
  },
  { brand: "UnionPay", icon: false, regex: /^(62[0-9]{14,17})$/ },
  { brand: "Visa", icon: "cc-visa", regex: /^4[0-9]{12}(?:[0-9]{3})?$/ },
  { brand: "Unknown", icon: false, regex: /.*/ },
];

const getValueFromEnum = ({ key, enumerator, enumList }) => {
  const enumDefinition = enumList.find(b => b.enum === enumerator);
  if (enumDefinition && enumDefinition[key]) {
    return billingCycle[key];
  }
  return "";
};

// return month description or short description from number
export const monthDescriptionFromNumber = (monthNumber, short = false) => {
  const monthInt =
    typeof monthNumber === "string" ? parseInt(monthNumber) : monthNumber;
  const month = months.find(m => m.number === monthInt);
  if (month) {
    if (!short) {
      return month.description;
    }
    return month.shortDescription;
  }
  return "";
};

// takes a number and returns a number ordinal (1st, 2nd, 3rd, etc.)
export const numberToOrdinal = value => {
  const numberValue = typeof value === "string" ? parseInt(value) : value;
  const nth = function(d) {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  return numberValue + nth(numberValue);
};

// converts a timestamp string to a js date object
export const timestampToDateObject = value => {
  const [year, month, day, hour, minute, second] = value.split(/[-,T,:]+/);
  const newDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
    parseInt(second)
  );
  return newDate;
};

// converts a timestamp string to a date in MM/dd/yyyy format
export const timestampToDateString = value => {
  if (!value || typeof value !== "string") {
    return "";
  }

  /** if timestamp with the letter T split the timestamp off then split by - */
  const [year, month, day] = value.includes("T")
    ? value.split("T")[0].split("-")
    : value.split("-");

  return `${month}/${day}/${year}`;
};

// converts a timestamp to a fancy date in "MMM Do" format
export const timestampToFancyDate = value => {
  if (!value || typeof value !== "string") {
    return "";
  }
  /** if timestamp with the letter T split the timestamp off then split by - */
  const [year, month, day] = value.includes("T")
    ? value.split("T")[0].split("-")
    : value.split("-");

  return `${monthDescriptionFromNumber(month, true)} ${numberToOrdinal(
    day
  )}, ${year}`;
};

// takes a billing cycle enum and returns the description or undefined if not found
export const getBillingCycleDescription = billingCycleEnum => {
  const billingCycle = billingCycleEnums.find(b => b.enum === billingCycleEnum);
  if (billingCycle) {
    return billingCycle.description;
  }
  return "";
};

// takes billing transaction type enum and returns description
export const getBillingTransactionTypeDescription = billingTransactionTypeEnum => {
  const transactionType = billingTransactionTypeEnums.find(
    b => b.enum === billingTransactionTypeEnum
  );
  if (transactionType) {
    return transactionType.description;
  }
  return "";
};

// returns credit card icon name based on brand or false if not found or doesn't have icon
export const getCreditCardIcon = brand => {
  const creditCard = creditCardBrands.find(i => i.brand === brand);
  if (creditCard && creditCard.icon) {
    return creditCard.icon;
  }
  return false;
};

// returns an array of month objects with the names and numbers of the 12 months of the year
export const getMonths = () => {
  const months = [];
  for (let m = 0; m < 12; m++) {
    const date = new Date(2020, m, 1);
    const month = date.toLocaleString("default", { month: "long" });
    const monthObject = { monthValue: m + 1 + "", monthName: month };
    months.push(monthObject);
  }
  return months;
};

// returns an array of year values starting from this year to 35 years from now
export const getYears = () => {
  const years = [];
  const date = new Date();
  const year = date.getFullYear();
  for (let offset = 0; offset < 36; offset++) {
    years.push(year + offset);
  }
  return years;
};

export const creditCardBrandFromNumber = creditCardNumber => {
  const number = creditCardNumber.replace(/-/g, "");
  if (number > 0) {
    for (let c = 0; c < creditCardBrands.length; c++) {
      if (creditCardBrands[c].regex.test(number)) {
        return creditCardBrands[c].brand;
      }
    }
  }
  return false;
};

// returns a credit card icon based on a credit card number
export const creditCardIconFromNumber = creditCardNumber => {
  const brand = creditCardBrandFromNumber(creditCardNumber);
  if (brand) {
    return getCreditCardIcon(brand);
  }
  return false;
};

// convert hex color code to rgb (#000000 to object with 0,0,0)
export function hexToRgb(hex) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])([a-f\d])?$/i;
  hex = hex.replace(shorthandRegex, function(m, r, g, b, a) {
    return r + r + g + g + b + b + (a ? a + a : "");
  });

  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(
    hex
  );
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: result[4] ? parseInt(result[4], 16) : 255,
      }
    : null;
}

// helper function for converting rgb
function componentToHex(c) {
  var hex = c.toString(16);
  return hex.length == 1 ? "0" + hex : hex;
}

// convert rgb values to hex 0,0,0 to #000000
export function rgbToHex(r, g, b) {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}
